import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Loader2, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";

/** Letzte 10 Auto-Match-Läufe mit Expand-Details. */
export function AutoMatchHistory() {
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    apiClient
      .get("/scheduler/bank-match/recent-runs?limit=10")
      .then((res) => setRuns(res.data || []))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Lade Historie ...
      </div>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <div className="text-xs text-slate-500 py-4 italic">
        Noch keine Läufe. Der erste Lauf findet heute Nacht um 02:45 Uhr statt — oder triggern
        Sie ihn manuell mit dem Button oben.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="auto-match-history">
      <div className="text-[11px] text-slate-500 mb-2">
        Die letzten {runs.length} Auto-Match-Läufe:
      </div>
      {runs.map((r) => {
        const isExpanded = expanded.has(r.run_id);
        const hasError = !!r.error || r.failed_count > 0;
        return (
          <div
            key={r.run_id}
            className={`rounded-md border ${
              hasError
                ? "border-amber-200 bg-amber-50/40"
                : r.applied_count > 0
                ? "border-emerald-200 bg-emerald-50/30"
                : "border-slate-200 bg-white"
            }`}
            data-testid={`auto-match-run-${r.run_id}`}
          >
            <button
              type="button"
              onClick={() => toggle(r.run_id)}
              className="w-full flex items-center gap-3 px-3 py-2 text-left"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-slate-900">
                    {fmtDate(r.started_at)}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {r.triggered_by === "manual" ? "Manuell" : "Automatisch"}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-600 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    {r.applied_count} angewendet
                  </span>
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Sparkles className="h-3 w-3" />
                    {r.skipped_low_score} übersprungen
                  </span>
                  {r.failed_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-rose-700">
                      <AlertTriangle className="h-3 w-3" />
                      {r.failed_count} Fehler
                    </span>
                  )}
                  <span className="text-slate-400">
                    · {r.companies_processed} Firma(en)
                  </span>
                </div>
              </div>
            </button>
            {isExpanded && (
              <div className="px-8 pb-3 text-[11px] space-y-2">
                {r.error && (
                  <div className="rounded bg-rose-50 text-rose-800 px-2 py-1.5">
                    <span className="font-medium">Fehler: </span>
                    {r.error}
                  </div>
                )}
                {(r.details || []).length === 0 ? (
                  <div className="text-slate-400 italic">
                    Keine Zuordnungen bei diesem Lauf.
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-slate-500 font-medium">Zuordnungs-Details:</div>
                    {(r.details || []).slice(0, 20).map((d, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-slate-700"
                      >
                        <span className="text-emerald-600">✓</span>
                        <span className="font-mono text-[10px] truncate">
                          {d.journal_entry_id}
                        </span>
                        <span className="text-slate-400">
                          · Score {d.score}
                        </span>
                      </div>
                    ))}
                    {(r.details || []).length > 20 && (
                      <div className="text-slate-400 italic">
                        ... und {r.details.length - 20} weitere.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AutoMatchHistory;
